/**
 * SegmentedTimeline
 *
 * Visual timeline showing all slides proportionally based on their duration.
 * Click on a segment to navigate to that slide.
 *
 * Features:
 * - Proportional width based on slide duration
 * - Visual distinction between slides and transitions
 * - Current position indicator
 * - Hover tooltips with slide info
 * - Click to seek
 */

import React, { useMemo, useCallback } from 'react';
import type { VideoManifest, TimelineSegment, VideoPlaybackState } from '@/types/videoManifest';
import { computeTimelineSegments } from '@/types/videoManifest';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export interface SegmentedTimelineProps {
  /** Video manifest with slides */
  manifest: VideoManifest | null;
  /** Current playback state */
  playbackState: VideoPlaybackState;
  /** Callback when a segment is clicked */
  onSeek?: (time: number) => void;
  /** Callback when a slide segment is clicked */
  onSlideClick?: (slideId: string) => void;
  /** Whether the timeline is interactive */
  interactive?: boolean;
  /** Show transition segments */
  showTransitions?: boolean;
  /** Additional class names */
  className?: string;
}

export const SegmentedTimeline: React.FC<SegmentedTimelineProps> = ({
  manifest,
  playbackState,
  onSeek,
  onSlideClick: _onSlideClick, // Kept for filmstrip use, not called from segment click
  interactive = true,
  showTransitions = true,
  className,
}) => {
  // Compute timeline segments from manifest
  const segments = useMemo(() => {
    if (!manifest) return [];
    return computeTimelineSegments(manifest);
  }, [manifest]);

  // Separate slides and transitions for better rendering
  const { slideSegments, slidesOnlyDuration } = useMemo(() => {
    const slides = segments.filter(s => !s.id.includes('-transition'));
    const slidesTotal = slides.reduce((sum, s) => sum + s.duration, 0);
    return { slideSegments: slides, slidesOnlyDuration: slidesTotal };
  }, [segments]);

  // Display segments with recalculated widths when transitions are hidden/shown as dividers
  const displaySegments = useMemo(() => {
    if (showTransitions) {
      // Show full segments with original widths
      return segments;
    }
    // Recalculate slide widths to fill 100% (excluding transition widths)
    // Transitions will be shown as thin dividers between slides
    return slideSegments.map(s => ({
      ...s,
      // Recalculate width based on slides-only duration
      widthPercent: slidesOnlyDuration > 0 ? (s.duration / slidesOnlyDuration) * 100 : 0,
    }));
  }, [segments, slideSegments, slidesOnlyDuration, showTransitions]);

  // Calculate playhead position - remapped when transitions are hidden
  const playheadPosition = useMemo(() => {
    const totalDuration = manifest?.metadata?.totalDuration || 1;
    const currentTime = playbackState.currentTime;

    if (showTransitions || slideSegments.length === 0) {
      // Simple proportional position
      return (currentTime / totalDuration) * 100;
    }

    // Remap position: find which slide we're in and calculate position within slides-only view
    let accumulatedSlideTime = 0;
    let accumulatedDisplayPercent = 0;

    for (const slide of slideSegments) {
      if (currentTime < slide.startTime) {
        // Before this slide (in a transition gap before first slide - rare)
        break;
      }

      if (currentTime >= slide.startTime && currentTime <= slide.endTime) {
        // Within this slide
        const slideProgress = (currentTime - slide.startTime) / slide.duration;
        const slideWidthPercent = (slide.duration / slidesOnlyDuration) * 100;
        return accumulatedDisplayPercent + (slideProgress * slideWidthPercent);
      }

      // Past this slide
      accumulatedSlideTime += slide.duration;
      accumulatedDisplayPercent += (slide.duration / slidesOnlyDuration) * 100;

      // Check if in transition after this slide
      // Get actual transition duration for this specific slide, not default
      const manifestSlide = manifest?.slides?.find(s => s.id === slide.id);
      const actualTransitionDuration = manifestSlide?.transitionOut?.duration
        || manifest?.defaultTransition?.duration
        || 0;
      const transitionEnd = slide.endTime + actualTransitionDuration;
      if (currentTime > slide.endTime && currentTime < transitionEnd) {
        // In transition - show playhead at end of previous slide
        return accumulatedDisplayPercent;
      }
    }

    // At end
    return 100;
  }, [manifest, playbackState.currentTime, showTransitions, slideSegments, slidesOnlyDuration]);

  // Find active segment
  const activeSegmentId = useMemo(() => {
    const currentTime = playbackState.currentTime;
    const activeSegment = segments.find(
      s => currentTime >= s.startTime && currentTime < s.endTime
    );
    return activeSegment?.id || null;
  }, [segments, playbackState.currentTime]);

  // Handle segment click
  const handleSegmentClick = useCallback((e: React.MouseEvent<HTMLDivElement>, segment: TimelineSegment) => {
    if (!interactive) return;

    // Calculate click position within the segment for precise seeking
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const segmentDuration = segment.endTime - segment.startTime;
    const seekTime = segment.startTime + (percentage * segmentDuration);

    // ONLY call onSeek - it handles loading the correct slide internally
    // Do NOT call onSlideClick as it causes a race condition with seek()
    // The seek() function in VideoCompositionController already calls goToSlideIndex
    onSeek?.(seekTime);
  }, [interactive, onSeek]);

  // Handle timeline bar click (for precise seeking)
  // When transitions are hidden, remap click position to real video time
  const handleTimelineClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!interactive || !manifest) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;

    if (showTransitions || slideSegments.length === 0) {
      // Simple proportional mapping
      const seekTime = percentage * manifest.metadata.totalDuration;
      console.log('[Timeline] Click seek (with transitions):', {
        clickPercent: (percentage * 100).toFixed(2),
        calculatedSeekTime: seekTime.toFixed(2),
        totalDuration: manifest.metadata.totalDuration.toFixed(2),
        showTransitions
      });
      onSeek?.(seekTime);
      return;
    }

    // Remap: find which slide this percentage falls into and calculate real video time
    let accumulatedPercent = 0;

    for (const slide of slideSegments) {
      const slideWidthPercent = (slide.duration / slidesOnlyDuration) * 100;
      const slideEndPercent = accumulatedPercent + slideWidthPercent;

      if (percentage * 100 <= slideEndPercent) {
        // Click is within this slide's display area
        const percentWithinSlide = (percentage * 100 - accumulatedPercent) / slideWidthPercent;
        const seekTime = slide.startTime + (percentWithinSlide * slide.duration);
        console.log('[Timeline] Click seek (remapped):', {
          clickPercent: (percentage * 100).toFixed(2),
          slideId: slide.id,
          slideStart: slide.startTime.toFixed(2),
          slideEnd: slide.endTime.toFixed(2),
          percentWithinSlide: (percentWithinSlide * 100).toFixed(2),
          calculatedSeekTime: seekTime.toFixed(2),
          slidesOnlyDuration: slidesOnlyDuration.toFixed(2),
          showTransitions
        });
        onSeek?.(seekTime);
        return;
      }

      accumulatedPercent = slideEndPercent;
    }

    // Click at end - seek to end of last slide
    const lastSlide = slideSegments[slideSegments.length - 1];
    if (lastSlide) {
      console.log('[Timeline] Click seek (end):', {
        clickPercent: (percentage * 100).toFixed(2),
        seekTime: lastSlide.endTime.toFixed(2)
      });
      onSeek?.(lastSlide.endTime);
    }
  }, [interactive, manifest, onSeek, showTransitions, slideSegments, slidesOnlyDuration]);

  // Format time for display
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!manifest || displaySegments.length === 0) {
    return (
      <div className={cn('h-3 md:h-2 bg-gray-200 dark:bg-zinc-800 rounded-full', className)} />
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className={cn(
          'relative h-3 md:h-2 flex rounded-full overflow-hidden',
          interactive && 'cursor-pointer',
          className
        )}
        onClick={handleTimelineClick}
      >
        {/* Segment bars */}
        {displaySegments.map((segment, index) => {
          const isTransition = segment.id.includes('-transition');
          const isActive = segment.id === activeSegmentId;
          // Show thin divider before slide (except first) when transitions are hidden
          const showDivider = !showTransitions && !isTransition && index > 0;

          return (
            <React.Fragment key={segment.id}>
              {/* Thin divider to indicate transition between slides */}
              {showDivider && (
                <div
                  className="h-full w-[2px] flex-shrink-0 bg-gray-400/50 dark:bg-zinc-600/50"
                  title="Transition"
                />
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={cn(
                      'h-full relative transition-opacity flex-shrink-0',
                      // Slide segment styling
                      !isTransition && [
                        'bg-primary/20',
                        isActive && 'bg-primary',
                        interactive && 'hover:bg-primary/40',
                      ],
                      // Transition segment styling (when shown)
                      isTransition && [
                        'bg-gray-300 dark:bg-zinc-700',
                        isActive && 'bg-gray-400 dark:bg-zinc-600',
                      ]
                    )}
                    style={{ width: `${segment.widthPercent}%` }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSegmentClick(e, segment);
                    }}
                  />
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  <div className="flex flex-col gap-0.5">
                    <span className="font-medium">{segment.title}</span>
                    <span className="text-white/60">
                      {formatTime(segment.startTime)} - {formatTime(segment.endTime)}
                    </span>
                  </div>
                </TooltipContent>
              </Tooltip>
            </React.Fragment>
          );
        })}

        {/* Playhead indicator - NO transition for immediate feedback */}
        <div
          className={cn(
            'absolute top-0 bottom-0 w-0.5 bg-white shadow-md',
            'transform -translate-x-1/2'
          )}
          style={{ left: `${playheadPosition}%` }}
        />
      </div>
    </TooltipProvider>
  );
};

/**
 * Compact version for inline use
 */
export const SegmentedTimelineCompact: React.FC<SegmentedTimelineProps> = (props) => {
  return (
    <SegmentedTimeline
      {...props}
      showTransitions={false}
      className={cn('h-1.5', props.className)}
    />
  );
};
